import JSZip from 'jszip';
import { isCostaRicaXmlDoc, parseCostaRicaXml } from './parseCostaRica';
import { isElSalvadorXmlDoc, parseElSalvadorJson, parseElSalvadorXml } from './parseElSalvador';

// Auto-detects and parses a batch of dropped/selected files into Costa Rica
// and El Salvador invoice buckets. This is the ONLY place that decides which
// country parser handles a given file — parseCostaRica.js and
// parseElSalvador.js are never modified to know about each other, so neither
// country's parsing behavior can regress from adding the other.
//
// Detection rules (each reuses that parser's own existing validation, rather
// than inventing new heuristics):
//   .zip   → unzip, recurse every .json/.xml entry through this same logic
//   .json  → Costa Rica has no JSON format, so .json is unambiguously El Salvador
//   .xml   → parsed once, then:
//              has <Emisor> AND <ResumenFactura>? → Costa Rica
//              else has a tipoDte-shaped tag?      → El Salvador
//              else                                 → unrecognized-schema error
//   other  → unsupported-extension error
export async function processComprasFiles(files) {
  const costaRica = [];
  const elSalvador = [];
  const voids = [];
  const errors = [];

  for (const file of Array.from(files)) {
    await handleFile(file, costaRica, elSalvador, voids, errors);
  }

  return { costaRica, elSalvador, voids, errors };
}

function displayName(file) {
  return file.webkitRelativePath || file.name;
}

async function handleFile(file, costaRica, elSalvador, voids, errors) {
  const fileName = displayName(file);
  const lower = fileName.toLowerCase();

  if (lower.endsWith('.zip')) {
    await handleZip(file, fileName, costaRica, elSalvador, voids, errors);
    return;
  }
  if (lower.endsWith('.json')) {
    const text = await file.text();
    handleJsonText(text, fileName, elSalvador, voids, errors);
    return;
  }
  if (lower.endsWith('.xml')) {
    const text = await file.text();
    handleXmlText(text, fileName, costaRica, elSalvador, voids, errors);
    return;
  }
  errors.push({ fileName, error: 'Tipo de archivo no soportado (usa .xml, .json o .zip).', country: null });
}

function handleJsonText(text, fileName, elSalvador, voids, errors) {
  const result = parseElSalvadorJson(text, fileName);
  if (result.ok) {
    elSalvador.push(...result.data.invoices);
    voids.push(...result.data.voids);
  } else {
    errors.push({ fileName, error: result.error, country: 'SV' });
  }
}

function handleXmlText(text, fileName, costaRica, elSalvador, voids, errors) {
  let doc;
  try {
    doc = new DOMParser().parseFromString(text, 'application/xml');
  } catch {
    errors.push({ fileName, error: 'XML inválido o malformado.', country: null });
    return;
  }
  if (doc.querySelector('parsererror')) {
    errors.push({ fileName, error: 'XML inválido o malformado.', country: null });
    return;
  }

  if (isCostaRicaXmlDoc(doc)) {
    const result = parseCostaRicaXml(text, fileName);
    if (result.ok) costaRica.push(result.data);
    else errors.push({ fileName, error: result.error, country: 'CR' });
    return;
  }

  if (isElSalvadorXmlDoc(doc)) {
    const result = parseElSalvadorXml(text, fileName);
    if (result.ok) {
      elSalvador.push(...result.data.invoices);
      voids.push(...result.data.voids);
    } else {
      errors.push({ fileName, error: result.error, country: 'SV' });
    }
    return;
  }

  errors.push({ fileName, error: 'XML no reconocido (ni Costa Rica ni El Salvador).', country: null });
}

async function handleZip(file, fileName, costaRica, elSalvador, voids, errors) {
  let zip;
  try {
    zip = await JSZip.loadAsync(await file.arrayBuffer());
  } catch {
    errors.push({ fileName, error: 'No se pudo abrir el archivo ZIP.', country: null });
    return;
  }

  const entries = Object.values(zip.files).filter((entry) => (
    !entry.dir
    && /\.(json|xml)$/i.test(entry.name)
    && !entry.name.startsWith('__MACOSX/')
    && !/(^|\/)\./.test(entry.name)
  ));
  if (!entries.length) {
    errors.push({ fileName, error: 'El ZIP no contiene archivos JSON o XML.', country: null });
    return;
  }

  for (const entry of entries) {
    const entryName = `${fileName}/${entry.name}`;
    const text = await entry.async('string');
    if (/\.json$/i.test(entry.name)) {
      handleJsonText(text, entryName, elSalvador, voids, errors);
    } else {
      handleXmlText(text, entryName, costaRica, elSalvador, voids, errors);
    }
  }
}
